import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { PresentationComment } from '../types';
import { COPY } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { PanelSection } from '../ui';

interface CommentsPanelProps {
  comments: PresentationComment[];
  canWrite: boolean;
  canResolve: boolean;
  onCreate: (body: string) => Promise<void>;
  onResolve: (id: string) => Promise<void>;
}

const CommentsPanel: React.FC<CommentsPanelProps> = ({ comments, canWrite, canResolve, onCreate, onResolve }) => {
  const [body, setBody] = useState('');
  const open = comments.filter((comment) => !comment.resolvedAt);
  const resolved = comments.filter((comment) => comment.resolvedAt);

  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary, mb: 1 }}>
        {COPY.comments}
      </Typography>
      {!comments.length && (
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary, mb: 2 }}>
          {COPY.commentEmpty}
        </Typography>
      )}
      {open.map((comment) => (
        <Box key={comment._id} sx={{ mb: 1.5, p: 1, border: `1px solid ${PRES.borderSubtle}`, borderRadius: 1 }}>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body }}>{comment.body}</Typography>
          {canResolve && (
            <Button size="small" onClick={() => void onResolve(comment._id)} sx={{ textTransform: 'none', mt: 0.5 }}>
              {COPY.resolveComment}
            </Button>
          )}
        </Box>
      ))}
      {!!resolved.length && (
        <PanelSection title={COPY.commentResolved} collapsible defaultOpen={false}>
          {resolved.map((comment) => (
            <Typography key={comment._id} sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mb: 0.75 }}>
              {comment.body}
            </Typography>
          ))}
        </PanelSection>
      )}
      {canWrite && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={COPY.commentPlaceholder}
          />
          <Button
            disabled={!body.trim()}
            onClick={async () => {
              await onCreate(body.trim());
              setBody('');
            }}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            {COPY.comments}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CommentsPanel;
